import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ExternalLink } from "lucide-react";

import {
  AUTHOR_LICENSES,
  CUSTOM_LICENSES,
  DEFAULT_LICENSE,
  LICENSES,
  LicenseCode,
  LicensedAuthor,
} from "../../../const/licenses";

const linkClass =
  "text-blue-500 hover:text-blue-700 font-medium transition-colors dark:text-indigo-300 dark:hover:text-indigo-200";

const Copyright: React.FC = () => {
  const { t } = useTranslation();

  // The default license is always listed (it covers "all other authors"),
  // followed by any license at least one author opted into.
  const listedCodes: LicenseCode[] = [
    DEFAULT_LICENSE,
    ...(Object.keys(AUTHOR_LICENSES) as LicenseCode[]).filter(
      (code) => code !== DEFAULT_LICENSE && (AUTHOR_LICENSES[code]?.length ?? 0) > 0
    ),
  ];
  const defaultInfo = LICENSES[DEFAULT_LICENSE];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-500 transition-colors mb-8 dark:hover:text-indigo-200"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.returnHome")}
        </Link>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-8 py-10 space-y-8">
          <div className="border-b border-gray-100 dark:border-gray-800 pb-6">
            <h1 className="text-left tracking-normal text-2xl font-black text-gray-900 dark:text-gray-50">
              {t("legal.copyrightTitle")}
            </h1>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {t("legal.lastUpdated")}: 01.10.2026
            </p>
          </div>

          {/* Licensing */}
          <Section title={t("legal.copyright.licensingTitle")}>
            <p>{t("legal.copyright.licensingBody1")}</p>
            <p>{t("legal.copyright.licensingBody2")}</p>
            <div className="rounded-xl border border-blue-100 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/40 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-500 dark:text-indigo-300">
                {t("legal.copyright.defaultLicense")}
              </p>
              <a href={defaultInfo.url} target="_blank" rel="noopener noreferrer" className={`${linkClass} inline-flex items-center gap-1 mt-0.5`}>
                {defaultInfo.code} — {defaultInfo.name}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <p>{t("legal.copyright.licensingBody3")}</p>
          </Section>

          {/* License types */}
          <Section title={t("legal.copyright.typesTitle")}>
            <div className="space-y-4">
              {listedCodes.map((code) => {
                const info = LICENSES[code];
                const authors = AUTHOR_LICENSES[code] ?? [];
                return (
                  <div key={code} className="rounded-xl border border-gray-100 dark:border-gray-800 px-4 py-3 space-y-1.5">
                    <a href={info.url} target="_blank" rel="noopener noreferrer" className={`${linkClass} inline-flex items-center gap-1 font-bold`}>
                      {info.code}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{info.name}</p>
                    <p>{t(`legal.copyright.licenses.${info.summaryKey}`)}</p>
                    <p>
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{t("legal.copyright.authors")}: </span>
                      {authors.length > 0 && <AuthorList authors={authors} />}
                      {code === DEFAULT_LICENSE && (
                        <>{authors.length > 0 ? ", " : ""}{t("legal.copyright.allOtherAuthors")}</>
                      )}
                    </p>
                  </div>
                );
              })}

              {CUSTOM_LICENSES.map((custom) => (
                <div key={custom.title} className="rounded-xl border border-gray-100 dark:border-gray-800 px-4 py-3 space-y-1.5">
                  <p className="font-bold text-gray-800 dark:text-gray-100">{custom.title}</p>
                  <p>{custom.terms}</p>
                  <p>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{t("legal.copyright.authors")}: </span>
                    <AuthorList authors={custom.authors} />
                  </p>
                </div>
              ))}
            </div>
          </Section>

          {/* For authors */}
          <Section title={t("legal.copyright.forAuthorsTitle")}>
            <p>
              {t("legal.copyright.forAuthorsBody")}{" "}
              <Link to="/contact" className={linkClass}>{t("legal.contactLink")}</Link>
            </p>
          </Section>

          {/* Disclaimer */}
          <Section title={t("legal.copyright.disclaimerTitle")}>
            <p>{t("legal.copyright.disclaimerBody")}</p>
          </Section>

          <div className="pt-4 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
            {t("legal.contactUs")}{" "}
            <Link to="/contact" className={linkClass}>
              {t("legal.contactLink")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthorList: React.FC<{ authors: LicensedAuthor[] }> = ({ authors }) => (
  <>
    {authors.map((a, i) => (
      <React.Fragment key={`${a.id ?? a.name}-${i}`}>
        {i > 0 && ", "}
        {a.id ? <Link to={`/authors/${a.id}/questions`} className={linkClass}>{a.name}</Link> : a.name}
      </React.Fragment>
    ))}
  </>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="space-y-2">
    <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</h2>
    <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default Copyright;
